import { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";

/**
 * Signed-in profile badge + dropdown menu. Sits to the right of the
 * Cart pill on desktop. The badge displays the email's first letter;
 * clicking opens a small menu with the full email, My Orders, and
 * Sign out. Hidden on mobile via media query — the hamburger menu
 * has its own account section.
 */
export function ProfileMenu() {
  const { user, go, signOut } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const initial = user.email.charAt(0).toUpperCase();

  return (
    <div className="hb-profile" ref={ref}>
      <button
        className="hb-profile-badge"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.email}`}
        onClick={() => setOpen((v) => !v)}
      >
        {initial}
      </button>
      {open && (
        <div className="hb-profile-menu" role="menu">
          <div className="hb-profile-menu-email" role="presentation">{user.email}</div>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              go({ name: "orders" });
            }}
          >
            My Orders
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
